import React, { useCallback, useContext } from 'react';
import type { FC } from 'react';

import { NewCommentsContext, ReactionsContext } from '@confluence/comment-context';
import { ErrorBoundary, Attribution } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import {
	MissedPubSubCommentEventsHandler,
	CommentEventType,
	type MissedCommentsResult,
} from '@confluence/comments-pubsub/entry-points/MissedPubSubCommentEventsHandler';
import {
	setUpPageCommentsPubSub,
	removePageCommentsPubSub,
	reloadAndAddPageComments,
} from '@confluence/comments-pubsub/entry-points/pageComments';
import type { PageCommentsReloadFn } from '@confluence/comments-pubsub/entry-points/pageComments';

type AutoReloadPageCommentsProps = {
	contentId: string;
	contentType?: string | null;
	lastPollTime: number;
	reloader: PageCommentsReloadFn;
};

export const AutoReloadPageComments: FC<AutoReloadPageCommentsProps> = ({
	contentId,
	contentType,
	lastPollTime,
	reloader,
}) => {
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const { addNewComment, removeComment, clearNewComments } = useContext(NewCommentsContext);

	const { userId } = useSessionData();

	const subscribeToPageCommentsPubSub = useCallback(() => {
		const queryArgs = {
			isReactionsEnabled,
		};

		setUpPageCommentsPubSub({
			contentId,
			contentType,
			userId,
			reloader,
			addNewComment,
			removeComment,
			queryArgs,
		});
	}, [contentId, contentType, userId, reloader, addNewComment, removeComment, isReactionsEnabled]);

	const unsubscribeFromPageCommentsPubSub = useCallback(() => {
		const queryArgs = {
			isReactionsEnabled,
		};

		removePageCommentsPubSub({
			contentId,
			contentType,
			userId,
			reloader,
			addNewComment,
			removeComment,
			queryArgs,
		});
	}, [contentId, contentType, userId, reloader, addNewComment, removeComment, isReactionsEnabled]);

	const handleEventFetchSuccess = useCallback(
		(data: MissedCommentsResult) => {
			if (data && data.contentId === contentId) {
				if (data.commentEvents.length) {
					const commentData = data.commentEvents.map((event) => {
						return { commentId: event.id, accountId: event.accountId };
					});
					void reloadAndAddPageComments(reloader, addNewComment, commentData, userId);
				}
			}
		},
		[contentId, addNewComment, reloader, userId],
	);

	if (!userId) {
		return null;
	} else {
		return (
			<ErrorBoundary attribution={Attribution.COLLABORATION}>
				<MissedPubSubCommentEventsHandler
					contentId={contentId}
					eventType={CommentEventType.FOOTER}
					lastPollTime={lastPollTime}
					subscribeToPubSubEvents={subscribeToPageCommentsPubSub}
					unsubscribeFromPubSubEvents={unsubscribeFromPageCommentsPubSub}
					onEventFetchSuccess={handleEventFetchSuccess}
					onDocumentReturn={clearNewComments}
					isAutoReloadPageComments
				/>
			</ErrorBoundary>
		);
	}
};
